import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { TOURNAMENT_CREATE_EXTERNAL_PAGE } from 'constants/pages';
import { getIsVairPro } from 'utils/shared/vair/vairSportStatus';
import { useModal } from 'hooks/useModal';
import { useVairConnection } from 'hooks/useVairConnection';
import { useVairPlayerSearch } from 'hooks/useVairPlayerSearch';
import { useViewer } from 'hooks/useViewer';
import ArrowRight from 'svg/ArrowRightV2';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonLink, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import AllEventsLearnMoreModal from './AllEventsLearnMoreModal';
import EventTypeCard from './EventTypeCard';
import EventTypeCardMobile from './EventTypeCardMobile';
import EventTypeLearnMoreModal from './EventTypeLearnMoreModal';
import VairAccessModal, { type VairAccessReason } from './VairAccessModal';
import { EVENT_TYPES, EVENT_TYPE_LEARN_MORE, type LearnMoreKey } from './data';
import { EventTypeModalProps } from './props';

const EventTypeModalContent: React.FC<EventTypeModalProps> = ({ isOpen, onClose, onOpen }) => {
  const router = useRouter();
  const [externalEventLink, setExternalEventLink] = useState('');
  const [learnMoreKey, setLearnMoreKey] = useState<LearnMoreKey | null>(null);
  const learnMoreModal = useModal();
  const allEventsLearnMoreModal = useModal();

  // VAIR event creation gate. Same checks as the VAIR Pro gate:
  // ambassador is member-level, VAIR Pro is per-sport. A connection is required
  // too — the players API resolves VAIR accounts through userVairConnection, so
  // without it there's no role to read. QuickCreatePage re-checks on arrival.
  const viewer = useViewer();
  const { vairUser, vairUserResolved } = useVairPlayerSearch();
  const { isConnected, loading: vairConnectionLoading, connectToVair } = useVairConnection();
  const isVairAmbassador = vairUser?.status.isAmbassador ?? false;
  const isVairPro = getIsVairPro(vairUser);
  const isVairAccessResolved = vairUserResolved && !vairConnectionLoading;

  const [vairAccess, setVairAccess] = useState<{
    reason: VairAccessReason;
    isRatingSession?: boolean;
  } | null>(null);
  const vairAccessModal = useModal();

  // Rating sessions are VAIR Pro only; other VAIR events allow ambassadors too.
  const getVairAccessReason = (isRatingSession?: boolean): VairAccessReason | null => {
    if (!viewer.userId) return 'signed-out';
    if (!isConnected) return 'not-connected';
    const hasRole = isRatingSession ? isVairPro : isVairAmbassador || isVairPro;
    return hasRole ? null : 'not-authorized';
  };

  // Entry point for every VAIR event CTA — from the cards or either learn-more
  // modal. Navigates when the user is eligible, otherwise swaps whichever modal
  // they're in for the explainer.
  const handleStartVairEvent = (href: string, isRatingSession?: boolean) => {
    if (!isVairAccessResolved) return;

    onClose();
    learnMoreModal.closeModal();
    allEventsLearnMoreModal.closeModal();

    const reason = getVairAccessReason(isRatingSession);
    if (!reason) {
      router.push(href);
      return;
    }

    setVairAccess({ reason, isRatingSession });
    vairAccessModal.openModal();
  };

  const handleCloseVairAccess = () => {
    vairAccessModal.closeModal();
    setVairAccess(null);
  };

  const handleVairAccessGoBack = () => {
    handleCloseVairAccess();
    onOpen();
  };

  const handleConnectVair = () => {
    handleCloseVairAccess();
    // Come back to the page they started from so the CTA is where they left it.
    connectToVair(router.asPath);
  };

  const handleCreateExternalListing = () => {
    const trimmed = externalEventLink.trim();
    if (!trimmed) return;
    const query = new URLSearchParams({ registrationUrl: trimmed }).toString();
    router.push(`${TOURNAMENT_CREATE_EXTERNAL_PAGE}?${query}`);
    onClose();
  };

  const onLearnMore = (key: LearnMoreKey) => {
    setLearnMoreKey(key);
    learnMoreModal.openModal();
    onClose();
  };

  const learnMoreContent = learnMoreKey ? EVENT_TYPE_LEARN_MORE[learnMoreKey] : null;

  const handleCloseLearnMore = () => {
    learnMoreModal.closeModal();
    setLearnMoreKey(null);
  };

  const handleGoBack = () => {
    handleCloseLearnMore();
    onOpen();
  };

  const handleOpenAllEventsLearnMore = () => {
    allEventsLearnMoreModal.openModal();
    onClose();
  };

  const handleAllEventsGoBack = () => {
    allEventsLearnMoreModal.closeModal();
    onOpen();
  };

  return (
    <>
      <Modal isOpen={isOpen} handleClose={onClose} classNameMaxWidth="max-w-[66rem]">
        {/* Mobile header */}
        <div className="flex flex-row items-center justify-between px-ds-lg py-ds-lg sm:hidden">
          <h2 className="typography-product-heading-mobile">Run an event using Bounce</h2>
          <ButtonText onClick={onClose} aria-label="Close">
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
        </div>

        {/* Desktop header */}
        <div className="hidden flex-row justify-end px-ds-xl py-5 sm:flex">
          <ButtonText onClick={onClose} aria-label="Close">
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
        </div>

        {/* Mobile body */}
        <div className="flex flex-col gap-ds-md px-ds-lg pb-ds-lg sm:hidden">
          {EVENT_TYPES.map((eventType) => (
            <EventTypeCardMobile
              key={eventType.title}
              title={eventType.title}
              isVairIntegrated={eventType.isVairIntegrated}
              icon={eventType.icon}
              getStartedHref={eventType.getStartedHref}
              isRatingSession={eventType.isRatingSession}
              onSelect={
                eventType.isRatingSession
                  ? () => handleStartVairEvent(eventType.getStartedHref, true)
                  : undefined
              }
              isLoading={eventType.isRatingSession && !isVairAccessResolved}
            />
          ))}
          <Button
            variant="secondary"
            size="lg"
            onClick={handleOpenAllEventsLearnMore}
            iconRight={<ArrowRight className="h-5 w-5" />}
          >
            Learn about events
          </Button>
          <ButtonLink
            href={TOURNAMENT_CREATE_EXTERNAL_PAGE}
            variant="primary"
            size="lg"
            iconLeft={<img className="h-4 w-4" src="/images/app/attach-gradient.svg" />}
            onClick={onClose}
          >
            List an external event
          </ButtonLink>
        </div>

        {/* Desktop body */}
        <div className="hidden flex-col gap-ds-3xl px-[5rem] pb-ds-3xl sm:flex">
          <h2 className="typography-product-heading-desktop text-center">
            Run an event using Bounce
          </h2>
          <div className="grid grid-cols-3 gap-ds-xl max-md:grid-cols-2">
            {EVENT_TYPES.map((eventType) => (
              <EventTypeCard
                key={eventType.title}
                title={eventType.title}
                isVairIntegrated={eventType.isVairIntegrated}
                icon={eventType.icon}
                getStartedHref={eventType.getStartedHref}
                isRatingSession={eventType.isRatingSession}
                learnMoreKey={eventType.learnMoreKey}
                onLearnMore={onLearnMore}
                onGetStarted={
                  eventType.isRatingSession
                    ? () => handleStartVairEvent(eventType.getStartedHref, true)
                    : undefined
                }
                isLoading={eventType.isRatingSession && !isVairAccessResolved}
              />
            ))}
          </div>
          <div className="flex w-full flex-col gap-2">
            <p className="typography-product-subheading text-center">
              Or list an external event on the marketplace
            </p>
            <div className="border-color-border-lightmode-primary flex w-full overflow-hidden rounded-full border bg-color-bg-lightmode-secondary">
              <input
                type="url"
                value={externalEventLink}
                onChange={(e) => setExternalEventLink(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateExternalListing()}
                placeholder="Paste event link here"
                className="typography-product-body min-w-0 flex-1 border-0 bg-transparent px-4 py-3 text-color-text-lightmode-primary placeholder:text-color-text-lightmode-tertiary focus:outline-none focus:ring-0"
                aria-label="Event registration link"
              />
              <Button
                size="md"
                onClick={handleCreateExternalListing}
                disabled={!externalEventLink.trim()}
                variant="primary"
                isInline
              >
                Create listing
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {learnMoreContent && (
        <EventTypeLearnMoreModal
          isOpen={learnMoreModal.isOpen}
          onClose={handleCloseLearnMore}
          title={learnMoreContent.title}
          description={learnMoreContent.description}
          icon={learnMoreContent.icon}
          iconClassName={learnMoreContent.iconClassName}
          getStartedHref={learnMoreContent.getStartedHref}
          onGoBack={handleGoBack}
          onGetStarted={
            learnMoreKey === 'ratingSession'
              ? () => handleStartVairEvent(learnMoreContent.getStartedHref, true)
              : undefined
          }
          isLoading={learnMoreKey === 'ratingSession' && !isVairAccessResolved}
        />
      )}

      <AllEventsLearnMoreModal
        isOpen={allEventsLearnMoreModal.isOpen}
        onClose={allEventsLearnMoreModal.closeModal}
        onGoBack={handleAllEventsGoBack}
        onStartVairEvent={handleStartVairEvent}
        isVairAccessResolved={isVairAccessResolved}
      />

      {vairAccess && (
        <VairAccessModal
          isOpen={vairAccessModal.isOpen}
          onClose={handleCloseVairAccess}
          reason={vairAccess.reason}
          isRatingSession={vairAccess.isRatingSession}
          onConnect={handleConnectVair}
          onGoBack={handleVairAccessGoBack}
        />
      )}
    </>
  );
};

// This modal is mounted globally by TopNav, so the content — and with it the VAIR
// role lookup — waits until the user has actually opened it once. It stays mounted
// after that, since closing it is how the VAIR explainer takes over.
const EventTypeModal: React.FC<EventTypeModalProps> = (props) => {
  const hasOpened = useRef(false);
  if (props.isOpen) hasOpened.current = true;

  if (!hasOpened.current) return null;

  return <EventTypeModalContent {...props} />;
};

export default EventTypeModal;
